import React, { useEffect, useRef, useState, useCallback } from "react";
import { MapPin, Search, Navigation, CheckCircle, Loader2 } from "lucide-react";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

// Module-level singleton loader so the Maps JS SDK is appended only once per
// page lifetime, even across StrictMode double-renders. We request the
// `places` library here because the Vendor Registration flow needs the
// Autocomplete widget — other maps consumers on the app (e.g. LiveTrackerMap)
// also benefit, but keep their own loader with no library collision risk.
let _mapsLoaderPromise = null;
const loadMaps = () => {
  if (_mapsLoaderPromise) return _mapsLoaderPromise;
  if (typeof window !== "undefined" && window.google?.maps?.places) {
    _mapsLoaderPromise = Promise.resolve(window.google);
    return _mapsLoaderPromise;
  }
  _mapsLoaderPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-doozygo-maps-places="1"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google));
      existing.addEventListener("error", reject);
      if (window.google?.maps?.places) resolve(window.google);
      return;
    }
    const cbName = "__doozygoMapsPlacesReady";
    window[cbName] = () => resolve(window.google);
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=places&v=weekly&loading=async&callback=${cbName}`;
    script.async = true;
    script.defer = true;
    script.dataset.doozygoMapsPlaces = "1";
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return _mapsLoaderPromise;
};

// Default India center so vendors not sharing location still see context.
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };
const DEFAULT_ZOOM = 5;
const PICKED_ZOOM = 16;

const ShopLocationPicker = ({ value, onChange }) => {
  const mapContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  // New Places API (AutocompleteSuggestion) — legacy Autocomplete widget no
  // longer works on keys without the deprecated Places API enabled.
  const placesLibRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);

  const [mapReady, setMapReady] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [gpsLoading, setGpsLoading] = useState(false);
  const [address, setAddress] = useState(value?.address || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  // Tracks whether vendor manually edited the address — once true, we stop
  // overwriting their text with reverse-geocoded suggestions on marker moves.
  const [addressEdited, setAddressEdited] = useState(Boolean(value?.address_edited));

  // Keep a live ref so async geocode callbacks always see the latest edit flag.
  const addressEditedRef = useRef(addressEdited);
  useEffect(() => { addressEditedRef.current = addressEdited; }, [addressEdited]);

  // Reverse-geocode a lat/lng → human address, but only if the vendor hasn't
  // started typing their own address yet.
  const reverseGeocode = useCallback((lat, lng) => {
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results?.[0] && !addressEditedRef.current) {
        const formatted = results[0].formatted_address;
        setAddress(formatted);
        // Push the suggested address up to the parent too, so the stored
        // payload reflects what the vendor currently sees.
        onChange((prev) => (prev ? { ...prev, address: formatted } : prev));
      }
    });
  }, [onChange]);

  const commit = useCallback((lat, lng, accuracy = 0, formatted = "") => {
    // When a search autocomplete returns a formatted address we treat it as
    // authoritative (vendor explicitly picked it), so reset the "edited" flag.
    if (formatted) {
      setAddress(formatted);
      setAddressEdited(false);
    }
    const resolvedAddress = formatted || (addressEditedRef.current ? address : "");
    const payload = {
      lat,
      lng,
      accuracy: Math.round(accuracy || 0),
      captured_at: new Date().toISOString(),
      address: resolvedAddress,
      address_edited: formatted ? false : addressEditedRef.current,
    };
    onChange(payload);
    if (!formatted) reverseGeocode(lat, lng);
  }, [onChange, address, reverseGeocode]);

  // Push manual address edits into the parent payload (debounced via onBlur
  // + onChange — React re-render loops are fine here, payload is tiny).
  const handleAddressChange = (e) => {
    const next = e.target.value;
    setAddress(next);
    setAddressEdited(true);
    onChange((prev) => (prev ? { ...prev, address: next, address_edited: true } : prev));
  };

  // Drop (or move) the draggable marker and pan the map to a point.
  const placeMarkerAt = useCallback((lat, lng) => {
    const map = mapRef.current;
    if (!map || !window.google?.maps) return;
    const stale = markerRef.current && markerRef.current.getMap?.() !== map;
    if (stale) {
      try { markerRef.current.setMap(null); } catch { /* ignore */ }
      markerRef.current = null;
    }
    if (!markerRef.current) {
      markerRef.current = new window.google.maps.Marker({ map, position: { lat, lng }, draggable: true });
      markerRef.current.addListener("dragend", () => {
        const p = markerRef.current.getPosition();
        commit(p.lat(), p.lng(), 0);
      });
    } else {
      markerRef.current.setPosition({ lat, lng });
    }
    map.panTo({ lat, lng });
    map.setZoom(PICKED_ZOOM);
  }, [commit]);

  // New Places API: fetch typeahead predictions (debounced) for our input.
  const fetchSuggestions = useCallback((input) => {
    const lib = placesLibRef.current;
    if (!lib?.AutocompleteSuggestion || !input || input.trim().length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    if (!sessionTokenRef.current && lib.AutocompleteSessionToken) {
      sessionTokenRef.current = new lib.AutocompleteSessionToken();
    }
    const req = {
      input: input.trim(),
      sessionToken: sessionTokenRef.current,
      includedRegionCodes: ["in"],
    };
    lib.AutocompleteSuggestion.fetchAutocompleteSuggestions(req)
      .then(({ suggestions: results }) => {
        const preds = (results || [])
          .filter((s) => s.placePrediction)
          .map((s) => ({
            text: s.placePrediction.text?.text || "",
            toPlace: () => s.placePrediction.toPlace(),
          }));
        setSuggestions(preds);
        setShowSuggestions(preds.length > 0);
      })
      .catch(() => { setSuggestions([]); setShowSuggestions(false); });
  }, []);

  const handleSearchInput = (e) => {
    const v = e.target.value;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 300);
  };

  const selectSuggestion = useCallback(async (sugg) => {
    setShowSuggestions(false);
    try {
      const place = sugg.toPlace();
      await place.fetchFields({ fields: ["location", "formattedAddress", "displayName"] });
      const loc = place.location;
      if (!loc) return;
      const lat = loc.lat();
      const lng = loc.lng();
      placeMarkerAt(lat, lng);
      commit(lat, lng, 0, place.formattedAddress || place.displayName || sugg.text || "");
      if (searchInputRef.current) searchInputRef.current.value = "";
      setSuggestions([]);
      sessionTokenRef.current = null; // end the billing session after a pick
    } catch { /* ignore */ }
  }, [commit, placeMarkerAt]);

  // Boot the map once the SDK is ready.
  useEffect(() => {
    let cancelled = false;
    loadMaps()
      .then((google) => {
        if (cancelled || !mapContainerRef.current) return;

        const startCenter = value?.lat && value?.lng ? { lat: value.lat, lng: value.lng } : DEFAULT_CENTER;
        const startZoom = value?.lat && value?.lng ? PICKED_ZOOM : DEFAULT_ZOOM;

        const map = new google.maps.Map(mapContainerRef.current, {
          center: startCenter,
          zoom: startZoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          gestureHandling: "greedy",
        });
        mapRef.current = map;
        geocoderRef.current = new google.maps.Geocoder();

        if (value?.lat && value?.lng) {
          const marker = new google.maps.Marker({
            map,
            position: { lat: value.lat, lng: value.lng },
            draggable: true,
          });
          markerRef.current = marker;
          marker.addListener("dragend", () => {
            const p = marker.getPosition();
            commit(p.lat(), p.lng(), 0);
          });
        }

        // Click-to-place / move marker
        map.addListener("click", (e) => {
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          // Heal a stale marker from a StrictMode / HMR remount before
          // reusing it — otherwise the marker would be attached to a
          // destroyed map and never render.
          const stale = markerRef.current && markerRef.current.getMap?.() !== map;
          if (stale) {
            try { markerRef.current.setMap(null); } catch { /* ignore */ }
            markerRef.current = null;
          }
          if (!markerRef.current) {
            markerRef.current = new google.maps.Marker({
              map,
              position: { lat, lng },
              draggable: true,
            });
            markerRef.current.addListener("dragend", () => {
              const p = markerRef.current.getPosition();
              commit(p.lat(), p.lng(), 0);
            });
          } else {
            markerRef.current.setPosition({ lat, lng });
          }
          map.panTo({ lat, lng });
          commit(lat, lng, 0);
        });

        // Load the NEW Places library (AutocompleteSuggestion / Place) for the
        // custom search dropdown bound to our styled input below.
        google.maps.importLibrary("places")
          .then((lib) => { placesLibRef.current = lib; })
          .catch(() => { /* search will no-op; map + GPS still work */ });

        setMapReady(true);
      })
      .catch(() => {
        setLoadError("Could not load Google Maps. Please refresh the page.");
      });
    return () => {
      cancelled = true;
      // Dispose the marker so a StrictMode / HMR remount doesn't leave
      // a dangling marker attached to a destroyed map instance.
      try { markerRef.current?.setMap(null); } catch { /* ignore */ }
      markerRef.current = null;
      mapRef.current = null;
    };
    // Only boot once — subsequent value changes are handled via the marker ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setLoadError("Your browser does not support GPS location.");
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        const map = mapRef.current;
        if (map && window.google?.maps) {
          // Heal any stale marker whose parent map was torn down on a
          // StrictMode / HMR remount, otherwise the "new" marker ends up on
          // a destroyed map and never renders.
          const stale = markerRef.current && markerRef.current.getMap?.() !== map;
          if (stale) {
            try { markerRef.current.setMap(null); } catch { /* ignore */ }
            markerRef.current = null;
          }
          if (!markerRef.current) {
            markerRef.current = new window.google.maps.Marker({
              map,
              position: { lat, lng },
              draggable: true,
            });
            markerRef.current.addListener("dragend", () => {
              const p = markerRef.current.getPosition();
              commit(p.lat(), p.lng(), 0);
            });
          } else {
            markerRef.current.setPosition({ lat, lng });
          }
          map.panTo({ lat, lng });
          map.setZoom(PICKED_ZOOM);
        }
        commit(lat, lng, accuracy);
        setGpsLoading(false);
      },
      (err) => {
        setLoadError(err.code === 1 ? "Please enable location permission in your browser." : "Could not get GPS — try again.");
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  return (
    <div className="space-y-2" data-testid="shop-location-picker">
      {/* Search bar */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8] z-10" />
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search your shop address, landmark or area"
          className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none text-sm"
          data-testid="shop-location-search"
          onChange={handleSearchInput}
          onFocus={() => { if (suggestions.length) setShowSuggestions(true); }}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
          autoComplete="off"
        />
        {showSuggestions && suggestions.length > 0 && (
          <ul
            className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-64 overflow-y-auto"
            data-testid="shop-location-suggestions"
          >
            {suggestions.map((s, i) => (
              <li
                key={`${i}-${s.text}`}
                onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s); }}
                className="px-3 py-2.5 text-sm text-[#1E1B4B] hover:bg-[#FFF1F6] cursor-pointer flex items-start gap-2 border-b border-gray-50 last:border-0"
                data-testid={`shop-location-suggestion-${i}`}
              >
                <MapPin size={13} className="text-[#E83E8C] mt-0.5 flex-shrink-0" />
                <span className="leading-snug">{s.text}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Map canvas */}
      <div className="relative rounded-xl overflow-hidden border border-gray-200" style={{ height: 280 }}>
        <div ref={mapContainerRef} className="w-full h-full" />
        {!mapReady && !loadError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 text-xs text-[#64748b]">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading map…
          </div>
        )}
        {loadError && (
          <div className="absolute inset-0 flex items-center justify-center bg-rose-50 text-xs text-rose-700 px-4 text-center">
            {loadError}
          </div>
        )}
      </div>

      {/* Actions + summary */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={useMyLocation}
          disabled={gpsLoading || !mapReady}
          className="flex-1 flex items-center justify-center gap-2 bg-[#1E1B4B] text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60"
          data-testid="shop-location-use-gps"
        >
          {gpsLoading ? <Loader2 size={14} className="animate-spin" /> : <Navigation size={14} />}
          {gpsLoading ? "Getting GPS…" : "Use my current location"}
        </button>
      </div>

      {value?.lat && value?.lng ? (
        <div className="space-y-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5" data-testid="shop-location-captured">
          <div className="flex items-start gap-2">
            <CheckCircle size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-emerald-700">Location pinned</p>
              <p className="text-[10px] text-[#64748b]">
                {value.lat.toFixed(6)}, {value.lng.toFixed(6)}
                {value.accuracy ? ` · ±${value.accuracy}m` : ""}
              </p>
            </div>
          </div>
          <div>
            <label className="text-[11px] font-medium text-[#1E1B4B] mb-1 flex items-center justify-between">
              <span>Shop Address (edit to add shop no., floor, landmarks)</span>
              {addressEdited && (
                <button
                  type="button"
                  onClick={() => {
                    setAddressEdited(false);
                    if (value?.lat && value?.lng) reverseGeocode(value.lat, value.lng);
                  }}
                  className="text-[10px] text-[#E83E8C] font-semibold hover:underline"
                  data-testid="shop-address-reset"
                >
                  Reset to map address
                </button>
              )}
            </label>
            <textarea
              value={address}
              onChange={handleAddressChange}
              placeholder="e.g. Shop 12, 2nd Floor, Krishna Plaza, near SBI ATM, Andheri West, Mumbai 400058"
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-emerald-200 bg-white focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none text-xs resize-none"
              data-testid="shop-address-input"
            />
            <p className="text-[10px] text-[#64748b] mt-1">
              Pin gives us GPS; this field helps delivery crews find the exact shop entrance.
            </p>
          </div>
        </div>
      ) : (
        <p className="text-[11px] text-[#64748b] flex items-center gap-1">
          <MapPin size={11} className="text-[#E83E8C]" />
          Search, tap the map, or use GPS to pin your shop. You can drag the marker to fine-tune.
        </p>
      )}
    </div>
  );
};

export default ShopLocationPicker;
